import React, { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { Button, LinearProgress, Table, TableBody, TableContainer, Typography } from '@mui/material'
import { CompanyEventRow } from './CompanyEventRow'
import SourceApi from '../../../api/SourceApi'
import { normalizedName } from '../../../components/entities/CompanyName'
import ConvertiblesApi, { ConvertibleInput } from '../../../api/ConvertiblesApi'
import { NewConvertibleQuickForm } from './NewConvertibleQuickForm'
import { ShareEntity } from 'types/ShareEntity'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { CompanyEntity } from 'types/CompanyEntity'
import { PRInputEntity } from '@finance-lab/utils/types'
import { SourceType } from 'types/SourceType'
import { isBefore } from 'date-fns'
import { SourceEntity } from 'types/SourceEntity'

type CompanyConvertiblesProps = {
    company: Pick<CompanyEntity, 'id' | 'name'>
    onReloadConvertibles: () => Promise<void>
    selectedTargets: Array<{ id: string }>
    convertibles: ConvertibleEntity[] | null
    sourceType: SourceType | null
    setSourceType: (sourceType: SourceType | null) => void
    selectedPR: PRInputEntity | null
    onArchivePR: (pr: PRInputEntity) => Promise<void>
}

async function addPRToConvertible(convertible: ConvertibleEntity, pr: PRInputEntity, sourceType: SourceType) {
    const sourceApi = new SourceApi()
    if (!convertible || !pr || !sourceType) {
        console.error(
            `addPRToConvertible: Missing ${convertible ? '' : 'convertible'} ${pr ? '' : 'pr'} ${sourceType ? '' : 'sourceType'}`,
        )
        return null
    }

    const newSource = {
        prId: pr.id,
        convertibleId: convertible.id,
        sourceType: sourceType,
    }
    const createdSource = await sourceApi.create(newSource)

    return createdSource as SourceEntity
}

export const CompanyConvertibles = ({
    company,
    onReloadConvertibles,
    selectedTargets,
    convertibles,
    sourceType,
    setSourceType,
    selectedPR,
    onArchivePR,
}: CompanyConvertiblesProps) => {
    const [newConvertibleFormOpen, setNewConvertibleFormOpen] = useState(false)

    const convertibleApi = new ConvertiblesApi()

    const handleCreateConvertible = async (share: ShareEntity, formData: Omit<ConvertibleInput, 'shareId'>) => {
        if (!company || !sourceType || !selectedPR) return
        const convertibleData = { ...formData, shareId: share.id }
        const result = await convertibleApi.create(convertibleData)

        if (result) {
            const sourceResult = await addPRToConvertible(result, selectedPR, sourceType)

            void onReloadConvertibles()

            if (sourceResult) {
                if (onArchivePR) await onArchivePR(selectedPR)
            }
        }
    }

    const enableQuickCreateButton = sourceType === 'Publication'

    if (!convertibles) return <LinearProgress />

    return (
        <>
            <Button
                variant='contained'
                color='primary'
                size='small'
                onClick={() => setNewConvertibleFormOpen(true)}
                disabled={!enableQuickCreateButton}
            >
                Ny konvertibel för {normalizedName(company.name)}
            </Button>
            <NewConvertibleQuickForm
                company={company}
                open={newConvertibleFormOpen}
                setOpen={setNewConvertibleFormOpen}
                handleCreateConvertible={handleCreateConvertible}
            />
            {convertibles.length === 0 ? <Typography variant='body1'>Inga konvertibler hittades</Typography> : null}

            {convertibles.length > 0 ? (
                <TableContainer>
                    <Table>
                        <TableBody>
                            {convertibles
                                .sort((a, b) => {
                                    if (!!a.completed && !!b.completed) {
                                        return isBefore(a.publicationDate ?? 0, b.publicationDate ?? 0) ? 1 : -1
                                    }
                                    return a.completed ? 1 : -1
                                })
                                .map((convertible) => {
                                    return (
                                        <CompanyEventRow
                                            key={convertible.id}
                                            companyId={company.id}
                                            event={convertible}
                                            eventType='convertible'
                                            selectedRows={selectedTargets}
                                            sourceType={sourceType}
                                            setSourceType={setSourceType}
                                            selectedPR={selectedPR}
                                            onCreateSource={addPRToConvertible}
                                            onArchivePR={onArchivePR}
                                        >
                                            <NavLink to={`/admin/convertibles/${convertible.id}`} target='_blank'>
                                                {convertible.convertibleName}
                                            </NavLink>
                                        </CompanyEventRow>
                                    )
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : null}
        </>
    )
}
